/**
 * useCompleteSetup - Hook for completing initial setup.
 *
 * Provides a mutation hook for completing the first-time setup
 * by creating the first admin user. On success, automatically logs
 * the user in by storing the returned credentials.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { completeSetup } from '@/lib/api';
import type { CompleteSetupRequest, CompleteSetupResponse } from '@/types/api';
import { setupQueryKeys } from './useSetupStatus';
import { authQueryKeys, storeToken, storeUser } from '@/features/auth/utils/authStorage';

/**
 * Hook for handling setup completion mutation.
 *
 * On success, the user is automatically logged in - the access token and user
 * info are stored, and the auth cache is updated.
 *
 * @returns Mutation object with:
 * - `mutate`: Function to trigger setup completion
 * - `mutateAsync`: Async function to trigger setup completion
 * - `isPending`: Boolean indicating if setup is in progress
 * - `error`: Error object if setup failed
 * - `isSuccess`: Boolean indicating if setup succeeded
 */
export function useCompleteSetup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: completeSetup,
    onSuccess: (data: CompleteSetupResponse) => {
      // Store auth credentials for auto-login
      storeToken(data.access_token);
      storeUser(data.user);

      // Update React Query auth cache
      queryClient.setQueryData(authQueryKeys.currentUser(), data.user);

      // Invalidate setup status query to refetch and reflect the new state
      queryClient.invalidateQueries({ queryKey: setupQueryKeys.status() });
    },
  });
}

export type { CompleteSetupRequest };
